import { ar } from "./ar.js";
import { en } from "./en.js";
export { ar, en };
export const LOCALES = ["ar", "en"];
export const DEFAULT_LOCALE = "ar";
export const DIRECTION = { ar: "rtl", en: "ltr" };
const DICTS = { ar, en };
let glossary = {};
/** يُستدعى مرة عند الإقلاع وعند كل حفظ لإعدادات المصطلحات. */
export function setGlossary(next) {
    glossary = next;
}
/**
 * المصطلحات المعروضة للمشتري في شاشة الإعدادات.
 *
 * **كلمات قصيرة فقط، لا جُمل.** ما يغيّره المشتري هو مفردات وكالته — «عميل»
 * تصير «براند»، و«عنصر خطة» يصير «بوست» — لا نصوص أخطاء ولا شروح. الجمل تبقى
 * من القاموس، وتتغيّر بترجمة لا بإعداد.
 *
 * المفردات الميدانية (عميل · عنصر خطة · مودريتور · بورد …) تنضمّ هنا مع نزول
 * موديولاتها. والآلية مبنية لكل المفاتيح أصلاً، فالانضمام عرضٌ لا بناء.
 */
export const EDITABLE_TERMS = [
    "common.save",
    "common.cancel",
    "common.delete",
    "common.edit",
    "common.add",
    "common.confirm",
    "common.search",
    "auth.signIn",
    "auth.signOut",
    "auth.password",
    "auth.email",
    "state.forbidden.request",
];
export const MAX_TERM_LENGTH = 48;
/** **وما لا يُفهَم يبقى نصّاً كما هو** — فقوسٌ مكسورٌ يُرى، لا يُسقط الشاشة */
function parseTemplate(template) {
    const parts = [];
    let text = "";
    let at = 0;
    const flush = () => {
        if (text !== "")
            parts.push({ kind: "text", text });
        text = "";
    };
    while (at < template.length) {
        const char = template[at] ?? "";
        if (char !== "{") {
            text += char;
            at += 1;
            continue;
        }
        const simple = /^\{(\w+)\}/.exec(template.slice(at));
        if (simple) {
            flush();
            parts.push({ kind: "param", name: simple[1] ?? "", raw: simple[0] });
            at += simple[0].length;
            continue;
        }
        const plural = readPlural(template, at);
        if (plural) {
            flush();
            parts.push(plural.part);
            at = plural.end;
            continue;
        }
        text += char;
        at += 1;
    }
    flush();
    return parts;
}
function readPlural(template, start) {
    const head = /^\{\s*(\w+)\s*,\s*plural\s*,/.exec(template.slice(start));
    if (!head)
        return null;
    const cases = new Map();
    let at = start + head[0].length;
    for (;;) {
        while (/\s/.test(template[at] ?? ""))
            at += 1;
        if (template[at] === "}")
            break;
        const label = /^(=\d+|zero|one|two|few|many|other)\s*\{/.exec(template.slice(at));
        if (!label)
            return null;
        at += label[0].length;
        let depth = 1;
        const from = at;
        while (at < template.length && depth > 0) {
            if (template[at] === "{")
                depth += 1;
            else if (template[at] === "}")
                depth -= 1;
            at += 1;
        }
        if (depth !== 0)
            return null;
        cases.set(label[1] ?? "", parseTemplate(template.slice(from, at - 1)));
    }
    if (!cases.has("other"))
        return null;
    const end = at + 1;
    return {
        part: { kind: "plural", name: head[1] ?? "", raw: template.slice(start, end), cases },
        end,
    };
}
/**
 * **وحالةُ العدد بقواعد العربية** — وهي تكفي الإنجليزية: فكلُّ حالةٍ لا صيغةَ لها في
 * القالب تقع إلى `other`، والإنجليزيةُ لا تكتب غيرَ `one` و`other`.
 */
const PLURAL = new Intl.PluralRules("ar");
function render(parts, values, count) {
    return parts
        .map((part) => {
        if (part.kind === "text") {
            return count === undefined ? part.text : part.text.replaceAll("#", count);
        }
        const value = values[part.name];
        if (value === undefined || value === null)
            return part.raw;
        if (part.kind === "param")
            return String(value);
        const n = Number(value);
        const shown = String(value);
        const chosen = (Number.isFinite(n) ? part.cases.get(`=${String(n)}`) : undefined) ??
            (Number.isFinite(n) ? part.cases.get(PLURAL.select(n)) : undefined) ??
            part.cases.get("other") ??
            [];
        return render(chosen, values, shown);
    })
        .join("");
}
function namesIn(parts, out) {
    for (const part of parts) {
        if (part.kind === "text")
            continue;
        if (!out.includes(part.name))
            out.push(part.name);
        if (part.kind === "plural")
            for (const inner of part.cases.values())
                namesIn(inner, out);
    }
}
/**
 * **مِلءُ القالب — بنسخةٍ واحدة** يقرؤها القاموسُ والصندوقُ والتنبيه.
 *
 * وكانت نسختين: هذه، وأختٌ في مُرسِل الإشعارات — **والصندوقُ لا يملأ بأيٍّ
 * منهما**، فظهر «{subject}» حرفياً في كلّ إشعارٍ يحمل اسماً.
 *
 * **وما لا قيمةَ له يبقى كما هو** لا يُمحى: فقوسٌ ظاهرٌ يقول إن قيمةً نقصت،
 * ومحوُه يُنتج جملةً ناقصةً تبدو صحيحة — وهو أخفى.
 */
export function fillTemplate(template, values) {
    if (!values)
        return template;
    return render(parseTemplate(template), values);
}
/**
 * **أقواسُ القالب بأسمائها** — بترتيب ظهورها، بلا تكرار.
 *
 * يقرؤها محرِّرُ صياغة الإشعارات (أيُّ حبّةٍ تُضاف) وكتالوجُ الأخطاء (ما يُخفى من
 * رسالةٍ تُعرَض بصيغتها) **بنفس القاعدة التي يملأ بها `fillTemplate`** — فلا
 * يرى أحدهما قوساً لا يراه الآخر.
 */
export function templateParams(...templates) {
    const out = [];
    for (const template of templates)
        namesIn(parseTemplate(template), out);
    return out;
}
/**
 * **رسالةٌ بصيغتها لا بحالتها** — كلُّ قوسٍ يصير «…».
 *
 * فكتالوجُ الأخطاء يعرض الرسالةَ كما يراها المستخدم، **ولا حالةَ بعينها هناك**:
 * «{names}» حرفياً اسمُ متغيّرٍ في وجه إنسان، و«…» يقول «هنا يُكتَب الاسم».
 */
export function templateShape(template, blank) {
    return fillTemplate(template, Object.fromEntries(templateParams(template).map((name) => [name, blank])));
}
const interpolate = (template, params) => fillTemplate(template, params);
/**
 * الترجمة — **الطريق الوحيد** لأي نصّ يراه المستخدم.
 *
 * `key` نوع لا نصّ، فلا يمكن طلب مفتاح غير موجود. ويمرّ كل نداء بطبقة تعديلات
 * المشتري أولاً، فالمصطلحات قابلة للتخصيص بلا مسار ثانٍ.
 */
export function translate(locale, key, params) {
    const override = glossary[key]?.[locale];
    return interpolate(override ?? DICTS[locale][key], params);
}
/**
 * للنصوص القادمة من السيرفر على السلك، حيث النوع ضاع.
 * تُرجع `undefined` لمفتاح غير معروف بدل أن ترجع نصاً فارغاً بصمت.
 */
export function tryTranslate(locale, key, params) {
    if (!(key in ar))
        return undefined;
    return translate(locale, key, params);
}
//# sourceMappingURL=index.js.map